import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transitions } from '../../transactions/transactionProcessBooking';
import { getSavedNotes } from '../SavedNotesPage/SavedNotesPage.duck';
import { updateUserListing } from '../../util/api';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const MARK_TRANSACTION_SEEN_REQUEST = 'app/InboxPage/MARK_TRANSACTION_SEEN_REQUEST';
export const MARK_TRANSACTION_SEEN_SUCCESS = 'app/InboxPage/MARK_TRANSACTION_SEEN_SUCCESS';
export const MARK_TRANSACTION_SEEN_ERROR = 'app/InboxPage/MARK_TRANSACTION_SEEN_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case MARK_TRANSACTION_SEEN_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case MARK_TRANSACTION_SEEN_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
      };
    case MARK_TRANSACTION_SEEN_ERROR:
      console.error(payload);
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const markTransactionSeenRequest = () => ({
  type: MARK_TRANSACTION_SEEN_REQUEST,
});

const markTransactionSeenSuccess = response => ({
  type: MARK_TRANSACTION_SEEN_SUCCESS,
  payload: response,
});

const markTransactionSeenError = e => ({
  type: MARK_TRANSACTION_SEEN_ERROR,
  error: true,
  payload: e,
});
// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const markTransactionSeen = ({ transactionId, isCustomerRole }) => async (dispatch, getState) => {
  dispatch(markTransactionSeenRequest());
  try {
    const state = getState();
    const tx = state.marketplaceData.entities.transaction?.[transactionId];

    if (!tx) return;

    const metadata = tx?.attributes?.metadata || {};

    const updateKey = isCustomerRole
      ? 'lastSeenByCustomerAt'
      : 'lastSeenByProviderAt';

    await updateUserListing({
      transactionId,
      role: isCustomerRole ? 'customer' : 'provider',
    });

    dispatch(
      addMarketplaceEntities({
        data: {
          data: [
            {
              id: tx.id,
              type: 'transaction',
              attributes: {
                metadata: {
                  ...metadata,
                  [updateKey]: new Date().toISOString(),
                },
              },
            },
          ],
        },
      })
    );

    dispatch(markTransactionSeenSuccess());

    // Refresh notification count in topbar after marking as seen
    dispatch(fetchCurrentUserNotifications());
  } catch (e) {
    dispatch(markTransactionSeenError(storableError(e)));
    throw e;
  }
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const { tab } = params;

  let currentUserr;
  if (getState().user?.currentUser) {
    currentUserr = getState().user?.currentUser;

  } else {
    currentUserr = await sdk.currentUser.show()
  }
  const isCompany = currentUserr && currentUserr.id && currentUserr?.attributes?.profile?.publicData.accountType === "company" || currentUserr?.data?.data?.attributes?.profile?.publicData?.accountType === "company";

  if (tab === "saved-notes") {
    const queryParams = parse(search);
    return dispatch(getSavedNotes(queryParams));
  }

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
    later: 'sale'
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    // only: (!isCompany && tab == 'orders') ? null : tab == 'later'? null : onlyFilter,
    // lastTransitions: tab == 'later' ? [transitions.SAVED_FOR_LATER,transitions.SAVED_AFTER_EXPIRE]: getAllTransitionsForEveryProcess(),
    processNames: ["default-booking"],
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata'
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName','profile.publicData'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
